"use strict";

exports.__esModule = true;
exports.TimelineValue = TimelineValue;
var _path2 = _interopRequireDefault(require("ramda/src/path"));
var _react = _interopRequireWildcard(require("react"));
var _styledComponents = require("styled-components");
var _Measures = require("../../Measures");
var _approximateFontSize = require("../../Measures/approximateFontSize");
var _fitLabel = require("../../Measures/fitLabel");
var _TileContext = require("../../Widgets/common/TileContext");
var _excluded = ["label", "width", "height", "timeline", "value", "color", "backgroundColor", "textAnchor", "fontSize"];
function _interopRequireWildcard(e, t) { if ("function" == typeof WeakMap) var r = new WeakMap(), n = new WeakMap(); return (_interopRequireWildcard = function _interopRequireWildcard(e, t) { if (!t && e && e.__esModule) return e; var o, i, f = { __proto__: null, "default": e }; if (null === e || "object" != typeof e && "function" != typeof e) return f; if (o = t ? n : r) { if (o.has(e)) return o.get(e); o.set(e, f); } for (var _t in e) "default" !== _t && {}.hasOwnProperty.call(e, _t) && ((i = (o = Object.defineProperty) && Object.getOwnPropertyDescriptor(e, _t)) && (i.get || i.set) ? o(f, _t, i) : f[_t] = e[_t]); return f; })(e, t); }
function _interopRequireDefault(e) { return e && e.__esModule ? e : { "default": e }; }
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (-1 !== e.indexOf(n)) continue; t[n] = r[n]; } return t; }
// Tile layout from the next-gen design: a large value with a small uppercase
// label under it, with breathing room inside the cell. With a background the tile is a
// card floating on a soft shadow. Short tiles put the label beside the value;
// an empty value shows a dash so the tile never looks broken. As a flat state
// line inside a device card the tile is a coloured dot and the value.
var HEADING_FONT = 'Archivo, "IBM Plex Sans", -apple-system, sans-serif';
var PLACEHOLDER = "—";
var PLACEHOLDER_COLOR = "#9FADB6";
var MIN_HEIGHT_FOR_LABEL = 36;
var ROOMY_HEIGHT_FOR_LABEL = 48;
var MIN_HEIGHT_FOR_COMPACT = 24;
var MIN_WIDTH_FOR_COMPACT = 90;
var COMPACT_LABEL_FONT_SIZE = 9.5;
var COMPACT_LABEL_SHARE = 0.5;
var COMPACT_GAP = 10;
var FALLBACK_SHADOW = "0 1px 2px rgba(29, 36, 41, 0.06), 0 6px 16px rgba(29, 36, 41, 0.08), 0 20px 40px -16px rgba(29, 36, 41, 0.14)";

/**
 * Displays a single value from a timeline.
 */
function TimelineValue(props) {
  var label = props.label,
    width = props.width,
    height = props.height,
    timeline = props.timeline,
    value = props.value,
    color = props.color,
    backgroundColor = props.backgroundColor,
    textAnchor = props.textAnchor,
    fontSize = props.fontSize,
    extra = _objectWithoutPropertiesLoose(props, _excluded);
  var theme = (0, _react.useContext)(_styledComponents.ThemeContext) || {};
  var tile = (0, _react.useContext)(_TileContext.TileContext) || {};
  var subscription = timeline && timeline.subscriptionProps || {};
  var custom = subscription.responseKey !== undefined && Boolean(subscription.element);
  var resolved = value == null ? undefined : custom ? subscription.responseKey ? (0, _path2["default"])(subscription.responseKey, value) : value : value.value;
  var isEmpty = resolved == null || resolved === "";
  var renderElement = function renderElement(_ref) {
    var w = _ref.width,
      h = _ref.height,
      anchor = _ref.anchor,
      size = _ref.size,
      weight = _ref.weight,
      font = _ref.font,
      textColor = _ref.textColor;
    if (isEmpty) {
      return /*#__PURE__*/_react["default"].createElement(_Measures.TextValue, {
        width: w,
        height: h,
        value: PLACEHOLDER,
        fontSize: size,
        fontWeight: 600,
        textAnchor: anchor,
        color: PLACEHOLDER_COLOR
      });
    }
    if (custom) {
      var RenderElement = subscription.element;
      return /*#__PURE__*/_react["default"].createElement(RenderElement, {
        width: w,
        height: h,
        value: resolved,
        fontSize: size,
        textAnchor: anchor,
        color: textColor
      });
    }
    return /*#__PURE__*/_react["default"].createElement(_Measures.TextValue, {
      width: w,
      height: h,
      value: JSON.stringify(resolved),
      fontSize: size,
      fontWeight: weight,
      fontFamily: font,
      textAnchor: anchor,
      color: textColor
    });
  };
  if (tile.flat) {
    var dot = backgroundColor && backgroundColor !== "transparent" ? backgroundColor : "#c7cfd5";
    var textWidth = Math.max(0, width - 15);
    return /*#__PURE__*/_react["default"].createElement(_TileContext.FlatRow, _extends({
      style: {
        width: width,
        height: height
      }
    }, extra), !isEmpty && /*#__PURE__*/_react["default"].createElement(_TileContext.FlatDot, {
      style: {
        background: dot
      }
    }), /*#__PURE__*/_react["default"].createElement("svg", {
      width: textWidth,
      height: height,
      style: {
        display: "block"
      }
    }, renderElement({
      width: textWidth,
      height: height,
      anchor: "start",
      size: 12.5,
      weight: 600,
      textColor: "#1B2227"
    })));
  }
  var padX = width >= 120 ? 14 : Math.max(4, Math.floor(width / 10));
  var padY = height >= 80 ? 12 : Math.max(3, Math.floor(height / 10));
  var innerWidth = Math.max(0, width - 2 * padX);
  var textColor = backgroundColor ? "#1B2227" : color;
  var valueColor = textColor || "currentColor";
  var hasLabel = typeof label === "string" && label.length > 0;
  var labelText = hasLabel ? label.toUpperCase() : undefined;
  var stacked = hasLabel && height >= MIN_HEIGHT_FOR_LABEL;
  var compact = hasLabel && !stacked && height >= MIN_HEIGHT_FOR_COMPACT && width >= MIN_WIDTH_FOR_COMPACT;
  var renderLabel = function renderLabel(_ref2) {
    var text = _ref2.text,
      x = _ref2.x,
      y = _ref2.y,
      size = _ref2.size,
      anchor = _ref2.anchor;
    return /*#__PURE__*/_react["default"].createElement("text", {
      x: x,
      y: y,
      fontSize: size,
      fontWeight: 700,
      letterSpacing: "0.07em",
      fill: valueColor,
      fillOpacity: 0.75,
      textAnchor: anchor,
      alignmentBaseline: "middle",
      dominantBaseline: "middle"
    }, text);
  };
  var body;
  if (compact) {
    var labelMaxWidth = innerWidth * COMPACT_LABEL_SHARE;
    var labelFontSize = Math.max(8, Math.min(COMPACT_LABEL_FONT_SIZE, (0, _approximateFontSize.approximateFontSize)({
      width: labelMaxWidth,
      height: 20,
      value: labelText
    })));
    var shownLabel = (0, _fitLabel.fitLabel)(labelText, labelFontSize, labelMaxWidth);
    var valueX = padX + (0, _fitLabel.estimateLabelWidth)(shownLabel, labelFontSize) + COMPACT_GAP;
    var valueWidth = Math.max(0, width - padX - valueX);
    var valueHeight = Math.max(0, height - 2 * padY);
    body = /*#__PURE__*/_react["default"].createElement(_react["default"].Fragment, null, renderLabel({
      text: shownLabel,
      x: padX,
      y: height / 2,
      size: labelFontSize,
      anchor: "start"
    }), /*#__PURE__*/_react["default"].createElement("svg", {
      x: valueX,
      y: padY,
      width: valueWidth,
      height: valueHeight,
      style: {
        overflow: "visible"
      }
    }, renderElement({
      width: valueWidth,
      height: valueHeight,
      anchor: "end",
      size: fontSize,
      weight: 700,
      font: HEADING_FONT,
      textColor: valueColor
    })));
  } else {
    var roomy = height >= ROOMY_HEIGHT_FOR_LABEL;
    var _labelFontSize = stacked ? Math.max(Math.min(roomy ? 10.5 : 9, innerWidth / (labelText.length * 0.75)), 7) : 0;
    var labelHeight = _labelFontSize > 0 ? roomy ? 16 : 12 : 0;
    var _valueHeight = Math.max(0, height - 2 * padY - labelHeight);
    var labelX = textAnchor === "start" ? padX : textAnchor === "end" ? width - padX : width / 2;
    body = /*#__PURE__*/_react["default"].createElement(_react["default"].Fragment, null, /*#__PURE__*/_react["default"].createElement("svg", {
      x: padX,
      y: padY,
      width: innerWidth,
      height: _valueHeight,
      style: {
        overflow: "visible"
      }
    }, renderElement({
      width: innerWidth,
      height: _valueHeight,
      anchor: textAnchor,
      size: fontSize,
      weight: 700,
      font: HEADING_FONT,
      textColor: valueColor
    })), stacked && renderLabel({
      text: (0, _fitLabel.fitLabel)(labelText, _labelFontSize, innerWidth),
      x: labelX,
      y: height - padY - labelHeight / 2,
      size: _labelFontSize,
      anchor: textAnchor || "middle"
    }));
  }
  var card = {
    display: "inline-block",
    verticalAlign: "top",
    boxSizing: "border-box",
    width: width,
    height: height
  };
  if (backgroundColor) {
    Object.assign(card, {
      borderRadius: 14,
      background: backgroundColor,
      boxShadow: theme.CardShadow || FALLBACK_SHADOW,
      transition: "background .4s ease"
    });
  }
  return /*#__PURE__*/_react["default"].createElement("div", {
    style: card
  }, /*#__PURE__*/_react["default"].createElement("svg", _extends({
    width: width,
    height: height
  }, extra, {
    style: Object.assign({
      display: "block"
    }, extra.style)
  }), body));
}